import { useEffect } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Divider, Drawer, Hidden } from '@mui/material';
import { BsCurrencyEuro } from 'react-icons/bs';
import useAuth from 'src/hooks/useAuth';
import CurrencyDollar from 'src/icons/CurrencyDollar';
import XIcon from '../../icons/X';
import BriefcaseIcon from '../../icons/Briefcase';
import CashIcon from '../../icons/Cash';
import ChartSquareBarIcon from '../../icons/ChartSquareBar';
import CollectionIcon from '../../icons/Collection';
import ClipboardListIcon from '../../icons/ClipboardList';
import HomeIcon from '../../icons/Home';
import LockIcon from '../../icons/Lock';
import ShareIcon from '../../icons/Share';
import UserIcon from '../../icons/User';
import UsersIcon from '../../icons/Users';
import Logo from '../Logo';
import NavSection from '../NavSection';
import Scrollbar from '../Scrollbar';
import CellTowerIcon from '@mui/icons-material/CellTower';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { PROJECTS_PAGE_PERMISSIONS } from 'src/pages/management/projects/projectConfig';
import Cog from 'src/icons/Cog';

interface DashboardSidebarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

const sections = [
  {
    title: 'General',
    items: [
      {
        title: 'Overview',
        path: '/',
        permissions: ['SEARCH_CUSTOMER'],
        icon: <ChartSquareBarIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'Management',
    items: [
      {
        title: 'Customers',
        path: '/customers',
        permissions: ['SEARCH_CUSTOMER'],
        icon: <UsersIcon fontSize="small" />,
      },
      {
        title: 'Organisations',
        path: '/organisations',
        permissions: ['SEARCH_ORGANISATION'],
        icon: <ShareIcon fontSize="small" />,
      },
      {
        title: 'Properties',
        path: '/properties',
        permissions: ['SEARCH_INSTALLATION'],
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: 'Devices',
        icon: <BriefcaseIcon fontSize="small" />,
        path: '/devices',
        permissions: ['SEARCH_DEVICE'],
      },
      {
        title: 'Users',
        path: '/users',
        permissions: ['SEARCH_USER'],
        icon: <UserIcon fontSize="small" />,
      },
      {
        title: 'Inventory',
        icon: <BriefcaseIcon fontSize="small" />,
        path: '/inventory',
        permissions: ['SEARCH_DEVICE_INVENTORY_LOG'],
      },

      {
        title: 'Orders',
        path: '/orders',
        permissions: ['SEARCH_CUSTOMER_ORDER'],
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: 'Invoice Search',
        path: '/invoicesearch',
        permissions: ['SEARCH_CUSTOMER_INVOICE'],
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: 'Pricing',
        icon: <BsCurrencyEuro fontSize="small" />,
        path: '/pricesets',
        permissions: ['SEARCH_PRICE_SET'],
      },
      {
        title: 'Projects',
        path: '/management/projects',
        // The same list the route guard requires, so the link and the route cannot disagree.
        permissions: PROJECTS_PAGE_PERMISSIONS,
        icon: <AssessmentIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'Tools',
    items: [
      {
        title: 'System Reports',
        path: '/tools/reports',
        permissions: ['SEARCH_REPORT'],
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: 'Device Contracts',
        path: '/tools/device-contracts',
        permissions: ['SEARCH_DEVICE_CONTRACT'],
        icon: <CashIcon fontSize="small" />,
      },
      {
        title: 'Device Operations',
        path: '/tools/device-operations',
        // Either permission opens the page — it hosts single-device provisioning (CREATE_DEVICE) and
        // bulk provisioning (BULK_PROVISION_DEVICE) as separately gated tabs. Requiring both here
        // would hide the nav entry from a bulk-only operator whom the route itself admits.
        permissions: ['CREATE_DEVICE', 'BULK_PROVISION_DEVICE'],
        permissionMode: 'any',
        icon: <BriefcaseIcon fontSize="small" />,
      },
      {
        title: 'Invoicing',
        path: '/tools/invoicing',
        permissions: ['SEARCH_CUSTOMER_INVOICE'],
        icon: <CurrencyDollar fontSize="small" />,
      },
      {
        title: 'Gateway Recon',
        path: '/tools/gateway-recon',
        permissions: ['SEARCH_DEVICE'],
        icon: <CellTowerIcon fontSize="small" />,
      },
      {
        title: 'Xero Settings',
        path: '/tools/xero-settings',
        permissions: ['UPDATE_XERO_TENANT'],
        icon: <Cog fontSize="small" />,
      }
    ],
  },
  {
    title: 'QuickCircuit',
    items: [
      {
        title: 'Orders',
        path: '/quickcircuit/orders',
        permissions: ['SEARCH_CUSTOMER_ORDER'],
        icon: <ClipboardListIcon fontSize="small" />,
      },
    ],
  },
  {
    title: 'User',
    items: [
      {
        title: 'SSO Providers',
        path: '/sso-providers',
        permissions: ['SEARCH_SSO_PROVIDER'],
        icon: <LockIcon fontSize="small" />,
      },
      {
        title: 'Manage Tether Users',
        path: 'accesskeys',
        permissions: ['SEARCH_ACCESS_KEY'],
        icon: <LockIcon fontSize="small" />,
      },
      {
        title: 'Manage Tether Roles',
        path: 'accessroles',
        permissions: ['SEARCH_ACCESS_KEY'],
        icon: <CollectionIcon fontSize="small" />,
      },
      {
        title: 'OAuth Clients',
        path: '/oauthclients',
        permissions: ['SEARCH_OAUTH_CLIENT'],
        icon: <LockIcon fontSize="small" />,
      },
      {
        title: 'Sign Out',
        path: 'authentication/logout',
        permissions: [],
        icon: <XIcon fontSize="small" />,
      },
    ],
  },
];

const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { onMobileClose, openMobile } = props;
  const location = useLocation();
  const { permissions } = useAuth();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Scrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <RouterLink to="/">
              <Logo
                sx={{
                  height: 40,
                  width: 40,
                }}
              />
            </RouterLink>
          </Box>
        </Hidden>
        <Divider />
        <Box sx={{ p: 2 }}>
          {sections.map((section) => {
            // Items list the permissions they need. Default is all-of; an item may set
            // permissionMode: 'any' when the destination is reachable by more than one permission
            // (Device Operations hosts separately gated tabs), so that a caller holding either one
            // still sees the link the route would let them through.
            const visibleItems = section.items.filter((item) =>
              (item as { permissionMode?: string }).permissionMode === 'any'
                ? item.permissions.some((p) => permissions.includes(p))
                : item.permissions.every((p) => permissions.includes(p))
            );

            if (visibleItems.length === 0) {
              return null;
            }
            return (
              <NavSection
                key={section.title}
                pathname={location.pathname}
                sx={{
                  '& + &': {
                    mt: 3,
                  },
                }}
                {...section}
                items={visibleItems}
              />
            );
          })}
        </Box>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              width: 280,
            },
          }}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          anchor="left"
          open
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              height: 'calc(100% - 64px) !important',
              top: '64px !Important',
              width: 280,
            },
          }}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
